import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Event as EventIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAppointments } from '../../hooks/useAppointments';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import {
  IAppointment,
  IAppointmentFiltersDTO,
} from '../../types/interfaces/IAppointment';

const UpcomingAppointmentsSlider: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuthStore();
  const { getUpcomingAppointments } = useAppointments();
  const { addNotification } = useUIStore();
  const [appointments, setAppointments] = useState<IAppointment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Cargar próximos turnos del día en curso
  useEffect(() => {
    const loadAppointments = async () => {
      try {
        const filters: IAppointmentFiltersDTO = {
          userId: user?.id || '',
          institutionId: user?.institution?.id || '',
          specialtyId: user?.specialty?.id || '',
        };

        const response = await getUpcomingAppointments(7, filters); // Obtener próximos 7 días
        if (response.success && response.data) {
          // Usar la fecha actual automáticamente
          const today = new Date();
          const todayStart = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );
          const todayEnd = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
            23,
            59,
            59
          );

          const todayAppointments = (response.data || []).filter(
            appointment => {
              const appointmentDate = new Date(appointment.start);
              return (
                appointmentDate >= todayStart && appointmentDate <= todayEnd
              );
            }
          );

          setAppointments(todayAppointments.slice(0, 5)); // Solo mostrar los próximos 5 del día
        }
      } catch (error) {
        addNotification({
          title: 'Error',
          message: 'Error al cargar los turnos',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadAppointments();
  }, []); // Remover dependencias problemáticas

  // Auto-slide cada 4 segundos
  useEffect(() => {
    if (appointments.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % appointments.length);
    }, 4000); // Cambiar cada 4 segundos

    return () => clearInterval(interval);
  }, [appointments.length]);

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % appointments.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      prev => (prev - 1 + appointments.length) % appointments.length
    );
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'Confirmado';
      case 'pending':
        return 'Pendiente';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Cargando turnos...
        </Typography>
      </Box>
    );
  }

  if (appointments.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <EventIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No hay turnos para hoy
        </Typography>
      </Box>
    );
  }

  const currentAppointment = appointments[currentIndex];

  return (
    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={600}>
          Turnos de Hoy
        </Typography>
      </Box>

      <Card
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}10)`,
          border: `1px solid ${theme.palette.primary.main}20`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2, pb: 1 }}>
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight={600} noWrap>
                {currentAppointment.patientName}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccessTimeIcon
                sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }}
              />
              <Typography variant="caption" color="text.secondary">
                {format(currentAppointment.start, 'EEEE dd/MM', {
                  locale: es,
                })}{' '}
                - {format(currentAppointment.start, 'HH:mm')}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {currentAppointment.type || 'Consulta'}
              </Typography>
              <Chip
                label={getStatusLabel(currentAppointment.status || 'pending')}
                size="small"
                color={
                  getStatusColor(currentAppointment.status || 'pending') as any
                }
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          </motion.div>
        </CardContent>

        {/* Controles de navegación */}
        {appointments.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
            <IconButton
              size="small"
              onClick={handlePrev}
              sx={{
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.main' + '15' },
              }}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {appointments.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor:
                      index === currentIndex ? 'primary.main' : 'text.disabled',
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>

            <IconButton
              size="small"
              onClick={handleNext}
              sx={{
                color: 'primary.main',
                '&:hover': { backgroundColor: 'primary.main' + '15' },
              }}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default UpcomingAppointmentsSlider;
